/**
 * The shared mech-portrait art resolver — the ONE place that decides what image (if any) a
 * mech's tile/card/thumbnail should show. Every display site (`MechHangar`, `GmRoster`,
 * `MechBuildSheet`, `DeployPickerBody`) calls this instead of hand-deriving a URL itself, so
 * the resolution rule lives once.
 *
 * **Three-tier resolution:** an uploaded `mech.image` wins over vendored chassis art
 * (`/frames/<frameId>.png`, the same source the compendium's `ChassisRender`/`compendiumView.ts`
 * renders — see `compendiumView.ts`'s `d.artUrl = \`/frames/${sel.id}.png\``), which wins over no
 * art at all.
 *
 * Distinct from `tokenArt.ts`'s battlefield tokens (`public/tokens/`) — this resolves the large
 * "portrait" art shown in menus/editors, not the top-down combat-map token. Don't merge the two.
 */

/**
 * The one template for a vendored chassis-art URL. {@link resolveMechArtUrl} and the compendium's
 * frame detail (`compendiumView.ts`, `d.artUrl`) both name this path shape; the precache sweep
 * ({@link ./assetPrecache}) has to name it a third time, so it lives here once rather than as three
 * string literals that can drift apart.
 */
export function frameArtUrl(frameId: string): string {
  return `/frames/${frameId}.png`;
}

/**
 * Every frame id with a mirrored PNG in `public/frames/`. Kept in lockstep with the files on disk
 * by `mechArt.test.ts` in both directions — same manifest-pin shape `tokenArt.ts`'s
 * `CHASSIS_TOKEN_IDS` uses, and for the same reason: a list that silently drifts from the
 * directory either 404s or quietly stops covering a file.
 *
 * **Provenance.** Every file is a mirror of Massif's CDN art for that frame **except
 * `mf_standard_pattern_i_everest.png`**. Massif's own Everest asset is a "NO IMAGE DATA"
 * placeholder, so ours is a substitute: the "GMS EVEREST (Assault Pattern)" pixel art from the
 * Retrograde Minis set (COMP/CON's `artistmap.json`; CDN `frames/pre_assault_pixel.png`), padded
 * to a square transparent canvas so the board's circle crop shows the whole mech. Re-mirroring
 * the CDN must not overwrite it with the placeholder again.
 */
export const MIRRORED_FRAME_IDS: readonly string[] = [
  "mf_atlas",
  "mf_balor",
  "mf_barbarossa",
  "mf_black_witch",
  "mf_blackbeard",
  "mf_caliban",
  "mf_deaths_head",
  "mf_drake",
  "mf_dusk_wing",
  "mf_genghis",
  "mf_goblin",
  "mf_gorgon",
  "mf_hydra",
  "mf_iskander",
  "mf_kobold",
  "mf_lancaster",
  "mf_lich",
  "mf_manticore",
  "mf_metalmark",
  "mf_minotaur",
  "mf_monarch",
  "mf_mourning_cloak",
  "mf_napoleon",
  "mf_nelson",
  "mf_pegasus",
  "mf_raleigh",
  "mf_saladin",
  "mf_sherman",
  "mf_standard_pattern_i_everest",
  "mf_sunzi",
  "mf_swallowtail",
  "mf_tokugawa",
  "mf_tortuga",
  "mf_vlad",
  "mf_zheng",
];

/** The minimal shape every call site can supply: an uploaded portrait URL and/or a picked frame id. */
export type MechArtSource = {
  frameId?: string | null;
  /** Uploaded portrait URL (`MechData.image`) — wins over chassis art when present and non-empty. */
  image?: string;
};

/**
 * Resolve the URL for a mech's portrait art, or `undefined` if there's nothing to show (no
 * upload and no frame picked yet). Callers render `undefined` as their existing glyph/text placeholder, and
 * must still attach an `onError` fallback for the case where the URL resolves but the file is
 * missing or fails to load (an uploaded file pruned from `<world>/uploads/`, or a frame without
 * mirrored art) — this function can't detect that, only the `<img>` load itself can.
 */
export function resolveMechArtUrl(mech: MechArtSource): string | undefined {
  if (mech.image) return mech.image;
  if (!mech.frameId) return undefined;
  return frameArtUrl(mech.frameId);
}
